/*
 * The basket, in two places: the panel (snippets/tcs-basket.liquid, <cart-drawer>) and the basket page
 * (sections/tcs-main-cart.liquid, <tcs-cart-page>). Both use the same lines (snippets/tcs-basket-line)
 * and the same behaviour: quantities change in place, and the basket only shows a change once the
 * server has confirmed it.
 *
 * <cart-drawer> is the element Wonder's product form (and the configurator app) hands a successful add
 * to. It keeps that contract: getSectionsToRender, renderContents, setActiveElement, data-cart-type.
 */
(() => {
  // What both share: change a line, render the sections the server sends back, report failures.
  class TcsBasketLines extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const share = event.target.closest('[data-basket-share]');
        if (share) {
          if (event.metaKey || event.ctrlKey || event.shiftKey) return;
          event.preventDefault();
          this.share(share);
          return;
        }
        const control = event.target.closest('[data-basket-quantity]');
        if (!control || control.disabled) return;
        this.change(control.closest('[data-line]'), Number(control.dataset.basketQuantity), control);
      });
    }

    // Share a line: a phone opens its own share sheet (Messages, WhatsApp, email…); anywhere else the
    // link is copied and the button says "Link copied" for a moment. The button answers for itself
    // because a toast would sit behind the open basket panel.
    async share(button) {
      const url = button.dataset.basketShare;
      const title = button.dataset.shareTitle;
      if (!url) return;
      const design = button.hasAttribute('data-share-design');
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        try {
          await navigator.share({ title, text: design ? `My ${title} design from The Cabinet Shop` : title, url });
        } catch (error) {
          // Closing the share sheet is not a failure.
        }
        return;
      }
      const label = button.querySelector('[data-share-label]');
      try {
        await navigator.clipboard.writeText(url);
      } catch (error) {
        window.prompt('Copy this link', url);
        return;
      }
      clearTimeout(button.shareTimer);
      button.setAttribute('data-copied', '');
      if (label) label.textContent = 'Link copied';
      button.shareTimer = setTimeout(() => {
        button.removeAttribute('data-copied');
        if (label) label.textContent = 'Share';
      }, 2400);
    }

    // [{ id, selector }]: the sections to request, and where each goes. Without a selector, the
    // section's whole contents replace #id's contents (used for the header's basket count).
    getSectionsToRender() {
      return [];
    }

    // Returns whether any of this element's own contents were replaced.
    render(sections) {
      if (!sections) return false;
      let rendered = false;
      this.getSectionsToRender().forEach(({ id, selector }) => {
        const html = sections[id];
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (selector) {
          const source = parsed.querySelector(selector);
          const target = this.querySelector(selector);
          if (!source || !target) return;
          target.replaceWith(source);
          rendered = true;
        } else {
          const source = parsed.querySelector('.shopify-section');
          const target = document.getElementById(id);
          if (source && target) target.innerHTML = source.innerHTML;
        }
      });
      this.classList.toggle('is-empty', !this.querySelector('[data-line]'));
      return rendered;
    }

    async change(line, quantity, control) {
      if (!line || line.hasAttribute('aria-busy')) return;
      const focusKey = line.dataset.key;
      const focusAction = control?.getAttribute('aria-label');
      line.setAttribute('aria-busy', 'true');
      this.showError('');

      try {
        const response = await fetch(`${window.routes.cart_change_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line.dataset.line),
            quantity,
            sections: [...new Set(this.getSectionsToRender().map((section) => section.id))],
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || 'That change didn’t go through.');
        }
        // The last line removed on the basket page: the page itself becomes the empty state.
        if (state.item_count === 0 && this.reloadWhenEmpty) {
          window.location.reload();
          return;
        }
        this.render(state.sections);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tcs-basket', cartData: state });
        }
        // Keep the keyboard where it was: the same control on the same line, or somewhere sensible.
        const sameLine = this.querySelector(`[data-key="${CSS.escape(focusKey)}"]`);
        const sameControl =
          sameLine && focusAction && sameLine.querySelector(`[aria-label="${CSS.escape(focusAction)}"]:not([disabled])`);
        (sameControl || this.fallbackFocus())?.focus({ preventScroll: true });
        if (quantity === 0) window.tcs?.toast({ title: 'Removed from your basket' });
      } catch (error) {
        line.removeAttribute('aria-busy');
        this.showError(error.message);
      }
    }

    fallbackFocus() {
      return this.querySelector('[data-line] [data-basket-quantity]:not([disabled])');
    }

    showError(message) {
      const box = this.querySelector('#TcsBasket-Error');
      if (!box) return;
      box.hidden = !message;
      box.textContent = message;
    }
  }

  /* ---- The panel ---- */

  if (!customElements.get('cart-drawer')) {
    class TcsBasket extends TcsBasketLines {
      connectedCallback() {
        super.connectedCallback();
        this.dialog = this.querySelector('dialog');
        this.activeElement = null;
        this.isCartPage = window.location.pathname === window.routes?.cart_url;

        // The header's basket button (and any other .wt-cart__trigger) opens the panel instead of navigating.
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('.wt-cart__trigger');
          if (!trigger || this.isCartPage || event.metaKey || event.ctrlKey) return;
          event.preventDefault();
          this.open(trigger);
        });

        document.addEventListener('cart-drawer:refresh', () => this.refresh());
      }

      // Wonder's section ids: the configurator app asks for these by name, so the panel answers to them.
      getSectionsToRender() {
        return [{ id: 'cart-drawer', selector: '#TcsBasket-Content' }, { id: 'cart-icon-bubble' }];
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      // Called by the product form after an add: the basket updates, and a confirmation drops from the
      // basket button with what was added. If that can't be shown, the basket opens as before.
      async renderContents(parsedState) {
        const rendered = this.render(parsedState?.sections);
        // An add that arrived without the basket's markup (another caller, other section ids): fetch it.
        if (!rendered) await this.refresh();
        if (!(await this.confirmAdded())) this.open(this.activeElement);
      }

      async confirmAdded() {
        const box = document.querySelector('[data-tcs-added]');
        if (!box) return false;
        let cart;
        try {
          const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
          cart = await response.json();
        } catch (error) {
          return false;
        }
        // The newest line comes first.
        const item = cart?.items?.[0];
        if (!item) return false;
        const money = (pence) => {
          try {
            return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: cart.currency || 'GBP' }).format(pence / 100);
          } catch (error) {
            return `£${(pence / 100).toFixed(2)}`;
          }
        };
        const image = box.querySelector('[data-tcs-added-image]');
        if (item.image) {
          image.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=160`;
          image.hidden = false;
        } else {
          image.hidden = true;
        }
        box.querySelector('[data-tcs-added-name]').textContent = item.product_title || item.title;
        const colour = item.properties?.Colour;
        box.querySelector('[data-tcs-added-name]').textContent = colour ? `${colour} sample` : item.product_title || item.title;
        box.querySelector('[data-tcs-added-meta]').textContent = colour ? 'Free colour sample' : item.quantity > 1 ? `Quantity ${item.quantity}` : 'Made to your design';
        box.querySelector('[data-tcs-added-price]').textContent = item.final_line_price === 0 ? 'Free' : money(item.final_line_price);
        if (colour && item.properties?._swatch) {
          image.src = item.properties._swatch;
          image.hidden = false;
        }
        box.querySelector('[data-tcs-added-view]').textContent = `View basket (${cart.item_count})`;
        // Every add passes here, whichever way it came (the product form, or the configurator app's own).
        // The configurator's phone drawer (assets/tcs-configurator-drawer.js) shows its own "Added to your
        // basket": it hears what was added, and cancels this event to keep the note away while it does.
        // The basket count acknowledges the add with one small pulse, wherever the confirmation shows.
        const count = document.querySelector('#cart-icon-bubble .tcs-header__count');
        if (count && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          setTimeout(() => count.animate?.([{ scale: 1 }, { scale: 1.25 }, { scale: 1 }], { duration: 380, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }), 60);
        }
        const added = new CustomEvent('tcs:added', {
          cancelable: true,
          detail: { name: box.querySelector('[data-tcs-added-name]').textContent, price: box.querySelector('[data-tcs-added-price]').textContent, count: cart.item_count },
        });
        if (!document.dispatchEvent(added)) return true;
        this.showAdded(box);
        return true;
      }

      showAdded(box) {
        clearTimeout(this.addedTimer);
        document.dispatchEvent(new CustomEvent('tcs:reveal-header'));
        box.hidden = false;
        box.removeAttribute('data-leaving');
        // Restart the arrival even if one is already showing.
        box.removeAttribute('data-shown');
        void box.offsetWidth;
        box.setAttribute('data-shown', '');
        const hide = () => {
          box.setAttribute('data-leaving', '');
          setTimeout(() => {
            if (box.hasAttribute('data-leaving')) box.hidden = true;
          }, 160);
        };
        const later = () => {
          clearTimeout(this.addedTimer);
          this.addedTimer = setTimeout(() => {
            if (!box.matches(':hover') && !box.contains(document.activeElement)) hide();
            else later();
          }, 6000);
        };
        later();
        if (!box.tcsWired) {
          box.tcsWired = true;
          box.addEventListener('click', (event) => {
            if (event.target.closest('[data-tcs-added-close]')) hide();
            if (event.target.closest('[data-tcs-added-view]')) {
              hide();
              this.open(document.querySelector('.wt-cart__trigger'));
            }
          });
          box.addEventListener('pointerleave', later);
          document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !box.hidden) hide();
          });
        }
      }

      open(opener) {
        if (!this.dialog || this.dialog.open) return;
        window.tcs?.open('tcs-basket', opener || document.activeElement);
      }

      async refresh() {
        const ids = this.getSectionsToRender().map((section) => section.id).join(',');
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${ids}`);
          this.render(await response.json());
        } catch (error) {
          // A stale basket is better than a broken one; the next change renders it afresh.
        }
      }

      fallbackFocus() {
        return super.fallbackFocus() || this.querySelector('.tcs-overlay__header [data-tcs-close]');
      }
    }
    customElements.define('cart-drawer', TcsBasket);
  }

  /* ---- The basket page ---- */

  if (!customElements.get('tcs-cart-page')) {
    class TcsCartPage extends TcsBasketLines {
      connectedCallback() {
        super.connectedCallback();
        this.reloadWhenEmpty = true;
      }

      // Only the lines and the totals: app blocks in the summary (e.g. Parcelify) stay as they are.
      getSectionsToRender() {
        const id = this.dataset.sectionId;
        return [
          { id, selector: '#TcsCart-Lines' },
          { id, selector: '#TcsCart-Totals' },
          { id: 'cart-icon-bubble' },
        ];
      }
    }
    customElements.define('tcs-cart-page', TcsCartPage);
  }
})();
